import { z } from 'zod'

/**
 * Planejamento de regime: Simples Nacional × Lucro Presumido × Lucro Real sobre a operação real da empresa.
 * Tributos sobre consumo vêm do motor (notas); IRPJ, CSLL e contribuição patronal vêm das premissas que só o
 * escritório conhece (folha, pró-labore, despesas ou lucro). Centavos e ppm, como no resto do motor.
 */

const Centavos = z.number().int().min(0)
const Ppm = z.number().int().min(0).max(1_000_000)

export const REGIMES_PLANEJAMENTO = ['SIMPLES', 'SIMPLES_HIBRIDO', 'PRESUMIDO', 'REAL'] as const
export type RegimePlanejamento = (typeof REGIMES_PLANEJAMENTO)[number]
export const ROTULO_REGIME_PLANEJAMENTO: Record<RegimePlanejamento, string> = {
  SIMPLES: 'Simples Nacional',
  SIMPLES_HIBRIDO: 'Simples híbrido',
  PRESUMIDO: 'Lucro Presumido',
  REAL: 'Lucro Real',
}

export const ATIVIDADES_PRESUNCAO = [
  'COMERCIO_INDUSTRIA',
  'SERVICOS',
  'SERVICOS_HOSPITALARES',
  'CONSTRUCAO_COM_MATERIAL',
  'CONSTRUCAO_SEM_MATERIAL',
  'TRANSPORTE_CARGA',
  'TRANSPORTE_PASSAGEIROS',
  'COMBUSTIVEIS',
] as const
export type AtividadePresuncao = (typeof ATIVIDADES_PRESUNCAO)[number]
export const ROTULO_ATIVIDADE_PRESUNCAO: Record<AtividadePresuncao, string> = {
  COMERCIO_INDUSTRIA: 'Comércio ou indústria (8% IRPJ · 12% CSLL)',
  SERVICOS: 'Serviços em geral e profissão regulamentada (32% IRPJ · 32% CSLL)',
  SERVICOS_HOSPITALARES: 'Serviços hospitalares, diagnóstico e terapia com Anvisa (8% IRPJ · 12% CSLL)',
  CONSTRUCAO_COM_MATERIAL: 'Construção por empreitada com todos os materiais (8% IRPJ · 12% CSLL)',
  CONSTRUCAO_SEM_MATERIAL: 'Construção só de mão de obra ou material parcial (32% IRPJ · 32% CSLL)',
  TRANSPORTE_CARGA: 'Transporte de carga (8% IRPJ · 12% CSLL)',
  TRANSPORTE_PASSAGEIROS: 'Transporte de passageiros (16% IRPJ · 12% CSLL)',
  COMBUSTIVEIS: 'Revenda de combustíveis (1,6% IRPJ · 12% CSLL)',
}

export const PremissasPlanejamento = z.object({
  /** salários dos empregados no ano (base da CPP, do RAT e de terceiros) */
  folhaAnual: Centavos.default(0),
  /** pró-labore dos sócios no ano (base dos 20% de contribuinte individual) */
  proLaboreAnual: Centavos.default(0),
  /** demais despesas dedutíveis no ano, além das compras e da folha (aluguel, energia, serviços, depreciação…) — só o Lucro Real usa */
  despesasOperacionaisAnuais: Centavos.default(0),
  /** lucro contábil antes de IRPJ e CSLL, quando o escritório já conhece: substitui a estimativa do Lucro Real */
  lucroAntesIrInformado: z.number().int().nullable().default(null),
  /** null = por tipo de receita: mercadoria, transporte (CT-e) e serviço pelo perfil da empresa */
  atividade: z.enum(ATIVIDADES_PRESUNCAO).nullable().default(null),
  /** anexo para simular o Simples; null = o do cadastro ou, fora do Simples, pela atividade e pelo fator R */
  anexoSimples: z.enum(['I', 'II', 'III', 'IV', 'V']).nullable().default(null),
  rat: z.enum(['LEVE', 'MEDIO', 'GRAVE']).default('MEDIO'),
  /** Fator Acidentário de Prevenção, de 0,5 a 2,0 (ppm: 1.000.000 = 1,0) */
  fap: z.number().int().min(500_000).max(2_000_000).default(1_000_000),
  /** outras entidades e fundos (salário-educação, Sistema S, INCRA) — depende do FPAS; padrão 5,8% */
  terceiros: Ppm.default(58_000),
  /** LC 224/2025: acréscimo de 10% na presunção acima de R$ 5 milhões (há liminares suspendendo) */
  aplicarLc224: z.boolean().default(true),
  /** ICMS médio das vendas quando as notas não mostram (empresa do Simples hoje); o crédito das compras vem sempre do XML */
  icmsVendas: Ppm.default(170_000),
  /** ISS quando as notas de serviço não mostram */
  iss: Ppm.default(50_000),
})
export type PremissasPlanejamento = z.infer<typeof PremissasPlanejamento>

export interface CargaRegime {
  regime: RegimePlanejamento
  elegivel: boolean
  motivoInelegivel: string | null
  /** cbs/ibs = débitos; creditos = crédito de CBS/IBS das compras; total já líquido */
  consumo: { pisCofins: number; icms: number; iss: number; ipi: number; cbs: number; ibs: number; creditos: number; das: number; total: number }
  renda: { irpj: number; adicionalIrpj: number; csll: number; baseIrpj: number; baseCsll: number; total: number }
  folha: { cpp: number; rat: number; terceiros: number; proLabore: number; total: number }
  total: number
  /** total sobre a receita (ppm) */
  sobreReceita: number
  /** Simples: anexo usado e fator R (ppm) */
  anexo: string | null
  fatorR: number | null
  /** Lucro Real: lucro antes de IRPJ/CSLL usado */
  lucroAntesIr: number | null
  observacoes: string[]
}

export interface AnoPlanejamento {
  ano: number
  rotulo: string
  regimes: CargaRegime[]
  melhor: RegimePlanejamento
  /** quanto o melhor regime economiza contra o regime atual (0 quando o atual já é o melhor) */
  economiaContraAtual: number
}

export interface ResultadoPlanejamento {
  receitaAnual: number
  comprasAnual: number
  regimeAtual: RegimePlanejamento | null
  atividade: AtividadePresuncao
  /** receita por atividade e o percentual de presunção de cada uma (Lei 9.249, art. 15 § 2º: atividades diversificadas) */
  presuncaoPorReceita?: Array<{ atividade: AtividadePresuncao; receita: number; irpj: number; csll: number }>
  /** perfil de serviço da empresa usado no cálculo */
  perfilServico?: string | null
  anos: AnoPlanejamento[]
  /** ICMS lido dos XML no ano (anualizado) e o que foi estimado */
  icmsNotas: {
    /** ICMS destacado nas compras para revenda ou insumo, fora as com substituição tributária — vira crédito fora do Simples */
    comprasDestacado: number
    /** crédito de ICMS destacado por fornecedor do Simples (vCredICMSSN) */
    comprasCreditoSimples: number
    /** ICMS destacado nas vendas */
    vendasDestacado: number
    /** vendas com ICMS já retido por ST, isentas ou não tributadas (sem ICMS próprio) */
    vendasSemIcmsProprio: number
    /** vendas com ICMS já recolhido por ST: no Simples, a parcela de ICMS sai do DAS */
    vendasComSt?: number
    /** true = ICMS das vendas estimado pela premissa (empresa do Simples ou notas sem destaque) */
    vendasEstimado: boolean
  }
  /** margem de lucro (antes de IRPJ/CSLL, sobre a receita) em que Real e Presumido empatam, por ano; null = sem empate possível */
  equilibrioRealPresumido: Array<{ ano: number; margem: number | null; margemAtual: number | null }>
  sensibilidade: Array<{ ano: number; rotulo: string; melhor: RegimePlanejamento; vira: boolean }>
  premissas: PremissasPlanejamento
  avisos: string[]
}
